<script setup lang="ts">
import { computed, watch } from 'vue'

import { Button, Overlay } from '@collector/shared-ui'
import { getParticipantTeamSide } from '@collector/shared-ui-domain'
import {
  useParticipantById,
  useSwitchTeamColors,
} from '@desktop/views/Relation/composables'
import PitchIncidentButton from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/PitchIncidentButton/PitchIncidentButton.vue'
import {
  useBlockades,
  useIncidentName,
} from '@desktop/views/Relation/Sports/composables'

import { useIncidentsRequirePointingPosition } from '../PitchWithChangePossession/PointOutIncidentBallPosition/useIncidentsRequirePointingPosition'
import { useAssignIncidentOnPitch } from './useAssignIncidentOnPitch'

interface Emits {
  visible: [value: boolean]
}

const emit = defineEmits<Emits>()

// composables
const { getIncidentName } = useIncidentName()
const { getTeamSideColor } = useSwitchTeamColors()
const { currentIncident: currentIncidentOnPitch } =
  useIncidentsRequirePointingPosition()
const { isPitchBlocked } = useBlockades()

// state
const { currentIncident, incidentsToDisplay, deleteIncident } =
  useAssignIncidentOnPitch()

const { participant: currentIncidentParticipant } = useParticipantById(
  () => currentIncident.value?.participantTeamId,
)

// computed
const currentIncidentName = computed(() =>
  currentIncident.value
    ? getIncidentName(currentIncident.value.incidentId)
    : null,
)

const teamSide = computed(() =>
  currentIncidentParticipant.value
    ? getParticipantTeamSide(currentIncidentParticipant.value)
    : null,
)

const teamSideColor = computed(
  () => teamSide.value && getTeamSideColor(teamSide.value),
)

const isCurrentIncindentOnPitchVisible = computed(() =>
  isPitchBlocked() ? false : currentIncidentOnPitch.value !== null,
)

const visible = computed(() =>
  !isCurrentIncindentOnPitchVisible.value && incidentsToDisplay.value
    ? currentIncident.value !== null
    : false,
)

const incidentCoordinates = computed(() => {
  if (!currentIncident.value) return undefined

  const { xPos, yPos } = currentIncident.value

  if (xPos === undefined || yPos === undefined) return undefined

  return { x: xPos, y: yPos }
})

// methods
async function closeOverlay(): Promise<void> {
  await deleteIncident()
}

function onClick(addIncident: () => void): void {
  addIncident()
}

// init
watch(visible, (visible) => emit('visible', visible), {
  immediate: true,
})
</script>

<template>
  <Overlay
    centered
    :modelValue="visible"
    @click.stop
  >
    <div class="@2md:gap-2 flex flex-col gap-1">
      <div class="flex flex-row-reverse">
        <Button
          class="@2md:h-6! @2md:w-6! @2md:min-w-6! @2md:min-h-6! bg-error-5 border-error-5 hover:bg-error-7 hover:border-error-7 text-neutral-10 h-5! min-h-5! w-5! min-w-5! cursor-pointer"
          icon="xmark"
          size="xs"
          square
          @click="closeOverlay"
        />
      </div>
      <div
        class="@2md:h-8 text-2xs flex h-6 w-full items-center pl-2 font-bold @md:text-xs"
        :class="{
          'bg-current-team-home-5': teamSide === 'home',
          'text-neutral-dark-0': teamSideColor === 'home',
          'bg-current-team-away-5': teamSide === 'away',
          'text-neutral-light-10': teamSideColor === 'away',
        }"
      >
        <span class="truncate">
          {{ currentIncidentName }}
        </span>
      </div>
      <div class="@2md:gap-2 flex justify-center gap-0.5">
        <template
          v-for="(incident, index) in incidentsToDisplay"
          :key="index"
        >
          <PitchIncidentButton
            :class="incident.class"
            :name="incident.name"
            :participant="currentIncidentParticipant ?? undefined"
            :incidentId="incident.incidentId"
            :variant="incident.variant"
            :coordinates="incidentCoordinates"
            @click="onClick"
          />
        </template>
      </div>
    </div>
  </Overlay>
</template>
